import { useState, useCallback, lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { AuthModalsProvider } from "@/contexts/AuthModalsContext";
import { CartProvider } from "@/contexts/CartContext";
import { LanguageProvider } from "@/contexts/LanguageContext";

import PageTransition from "@/components/PageTransition";
import WebMCPProvider from "@/components/WebMCPProvider";
import LoadingScreen from "@/components/LoadingScreen";

// Lazy-loaded non-critical components
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const CartDrawer = lazy(() => import("@/components/CartDrawer"));
const AccessibilityWidget = lazy(() => import("./components/AccessibilityWidget.tsx"));

// Critical route - eagerly loaded
import Index from "./pages/Index.tsx";

// Lazy-loaded routes
const CookiePolicy = lazy(() => import("./pages/CookiePolicy.tsx"));
const Reviews = lazy(() => import("./pages/Reviews.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const PlatformPage = lazy(() => import("./pages/PlatformPage.tsx"));
const SubCategoryPage = lazy(() => import("./pages/SubCategoryPage.tsx"));
const ProductPage = lazy(() => import("./pages/ProductPage.tsx"));
const BlogPage = lazy(() => import("./pages/BlogPage.tsx"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage.tsx"));
const Checkout = lazy(() => import("./pages/Checkout.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const TopUp = lazy(() => import("./pages/TopUp.tsx"));
const CreditsHistory = lazy(() => import("./pages/CreditsHistory.tsx"));
const OrdersHistory = lazy(() => import("./pages/OrdersHistory.tsx"));
const SitemapPage = lazy(() => import("./pages/SitemapPage.tsx"));
const TermsPage = lazy(() => import("./pages/TermsPage.tsx"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage.tsx"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage.tsx"));
const DynamicPage = lazy(() => import("./pages/DynamicPage.tsx"));

const queryClient = new QueryClient();

const App = () => {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
  const [loading, setLoading] = useState(!isMobile);
  const handleLoadComplete = useCallback(() => setLoading(false), []);

  return (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Suspense fallback={null}>
          <Toaster />
          <Sonner />
        </Suspense>
        {loading && <LoadingScreen onComplete={handleLoadComplete} />}
        <BrowserRouter>
          <LanguageProvider>
            <AuthProvider>
              <AuthModalsProvider>
                
                <CartProvider>
                  <Suspense fallback={null}>
                    <CartDrawer />
                  </Suspense>
                  <WebMCPProvider />
                  <PageTransition>
                    <Suspense fallback={null}>
                      <Routes>
                        <Route path="/" element={<Index />} />
                        <Route path="/cookie-policy" element={<CookiePolicy />} />
                        <Route path="/reviews" element={<Reviews />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/topup" element={<TopUp />} />
                        <Route path="/credits-history" element={<CreditsHistory />} />
                        <Route path="/orders" element={<OrdersHistory />} />
                        <Route path="/admin" element={<Admin />} />
                        <Route path="/blog" element={<BlogPage />} />
                        <Route path="/blog/:platformSlug" element={<BlogPage />} />
                        <Route path="/blog/:platformSlug/:categorySlug" element={<BlogPage />} />
                        <Route path="/blog/post/:postSlug" element={<BlogPostPage />} />
                        <Route path="/:platformSlug" element={<PlatformPage />} />
                        <Route path="/:platformSlug/category/:categorySlug" element={<SubCategoryPage />} />
                        <Route path="/:platformSlug/:productSlug" element={<ProductPage />} />
                        <Route path="/sitemap" element={<SitemapPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                        <Route path="/accessibility" element={<AccessibilityPage />} />
                        <Route path="/page/:pageSlug" element={<DynamicPage />} />
                        <Route path="/unsubscribe" element={<Unsubscribe />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </PageTransition>
                  <Suspense fallback={null}>
                    <AccessibilityWidget />
                  </Suspense>
                </CartProvider>
                
              </AuthModalsProvider>
            </AuthProvider>
          </LanguageProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
  );
};

export default App;
